import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getPriceHistory } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { LOGO_URL } from "@/constants/network";

const RANGES = ["1h", "24h", "7d"];
const MARGIN = { top: 4, right: 0, left: 0, bottom: 0 };
const CURSOR = { stroke: "#2A2411" };
const X_DOMAIN = ["dataMin", "dataMax"];
const fmtTime = (t, range) =>
  new Date(t * 1000).toLocaleString(undefined, range === "7d" ? { month: "short", day: "numeric", hour: "2-digit" } : { hour: "2-digit", minute: "2-digit" });

const ChartTooltip = ({ active, payload, range }) =>
  active && payload?.length ? (
    <div className="rounded-md border border-[#2A2411] bg-[#050505] px-3 py-2 text-xs">
      <p className="text-[#D4AF37] font-mono font-bold">{payload[0].value.toFixed(4)} USDT</p>
      <p className="text-zinc-500">{fmtTime(payload[0].payload.t, range)}</p>
    </div>
  ) : null;

// Keeps at least ±0.5% around the price so tiny moves don't render as tall spikes
const padDomain = ([lo, hi]) => {
  const pad = Math.max((hi - lo) * 0.2, ((hi + lo) / 2) * 0.005);
  return [lo - pad, hi + pad];
};

export const PriceChart = () => {
  const [range, setRange] = useState("24h");
  const { data, isError } = useQuery({
    queryKey: ["priceHistory", range], queryFn: () => getPriceHistory(range), refetchInterval: 6000,
  });
  const up = (data?.changePercent ?? 0) >= 0;

  return (
    <Card data-testid="price-chart" className="border-[#2A2411] bg-[#0A0A0A]/80 mt-6">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <img src={LOGO_URL} alt="FMI" className="w-4 h-4 rounded-full" /> FMI / USDT · FSC pool
            </div>
            <p data-testid="price-chart-current" className="font-display text-2xl font-bold text-white mt-1">
              {data ? data.current.toFixed(4) : "—"} <span className="text-sm text-zinc-500">USDT</span>
            </p>
            {data && (
              <p className="text-xs font-mono whitespace-nowrap">
                <span data-testid="price-chart-change" className={up ? "text-green-400" : "text-red-400"}>{up ? "+" : ""}{data.changePercent}%</span>
                <span data-testid="price-chart-volume" className="text-zinc-500"> · {data.trades} trades · {data.volumeUsdt.toLocaleString()} USDT</span>
              </p>
            )}
          </div>
          <div className="flex gap-1 rounded-full border border-[#2A2411] p-0.5">
            {RANGES.map((r) => (
              <button key={r} data-testid={`price-chart-range-${r}`} onClick={() => setRange(r)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${range === r ? "bg-[#D4AF37] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="h-40">
          {isError ? (
            <p data-testid="price-chart-error" className="text-sm text-zinc-500 pt-14 text-center">Price history unavailable</p>
          ) : data ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.points} margin={MARGIN}>
                <defs>
                  <linearGradient id="fmiPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" type="number" domain={X_DOMAIN} hide />
                <YAxis domain={padDomain} hide />
                <Tooltip content={<ChartTooltip range={range} />} cursor={CURSOR} />
                <Area type="stepAfter" dataKey="price" stroke="#D4AF37" strokeWidth={2} fill="url(#fmiPrice)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full animate-pulse rounded bg-[#050505]" />
          )}
        </div>
        {data && (
          <div className="flex justify-between text-[11px] text-zinc-600 font-mono mt-2">
            <span>L {data.low.toFixed(4)}</span><span>H {data.high.toFixed(4)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
